import * as React from "react";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { IconTile } from "components/ui/icon-tile";
import type { Tone } from "components/ui/tone";

/* ══════════════════════════════════════════════════════════════════════════
   useConfirm — a promise-based replacement for window.confirm().

   `window.confirm` was used at 11 destructive call sites. It renders OS chrome
   that ignores the theme, cannot be styled, cannot explain consequences, and
   blocks the main thread. This keeps the same one-line ergonomics:

       if (!(await confirm({ title: "Delete this segment?" }))) return;

   …while rendering an accessible, themed dialog that can name what is about
   to be destroyed and label the button with the actual verb.
   ══════════════════════════════════════════════════════════════════════════ */

export interface ConfirmOptions {
    title: React.ReactNode;
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** `danger` for destructive actions — the default */
    tone?: Extract<Tone, "danger" | "warning" | "brand" | "info">;
    icon?: React.ElementType;
    /** Extra content between the description and the buttons */
    body?: React.ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

const DEFAULT_ICON: Record<string, React.ElementType> = {
    danger: Trash2,
    warning: AlertTriangle,
    brand: Info,
    info: Info,
};

interface PendingState extends ConfirmOptions {
    resolve: (value: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [pending, setPending] = React.useState<PendingState | null>(null);

    const confirm = React.useCallback<ConfirmFn>(
        (options) =>
            new Promise<boolean>((resolve) => {
                setPending({ ...options, resolve });
            }),
        []
    );

    const settle = React.useCallback(
        (result: boolean) => {
            setPending((current) => {
                current?.resolve(result);
                return null;
            });
        },
        []
    );

    const tone = pending?.tone ?? "danger";
    const Icon = pending?.icon ?? DEFAULT_ICON[tone] ?? AlertTriangle;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            <Dialog open={!!pending} onOpenChange={(open) => !open && settle(false)}>
                {pending && (
                    <DialogContent size="sm" showClose={false} padding={false} className="sm:max-w-[26rem]">
                        <div className="p-5 sm:p-6">
                            <div className="flex items-start gap-3.5">
                                <IconTile tone={tone} size="lg">
                                    <Icon strokeWidth={2} />
                                </IconTile>
                                <div className="min-w-0 flex-1 pt-0.5">
                                    <DialogTitle className="text-title-sm text-content">
                                        {pending.title}
                                    </DialogTitle>
                                    {pending.description && (
                                        <DialogDescription className="mt-1.5 text-ui text-content-muted leading-relaxed">
                                            {pending.description}
                                        </DialogDescription>
                                    )}
                                </div>
                            </div>

                            {pending.body && <div className="mt-4">{pending.body}</div>}

                            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                                <Button variant="secondary" onClick={() => settle(false)}>
                                    {pending.cancelLabel ?? "Cancel"}
                                </Button>
                                <Button
                                    // Confirm is autofocused so Enter completes the
                                    // action and Escape still cancels.
                                    autoFocus
                                    variant={tone === "danger" ? "destructive" : "primary"}
                                    onClick={() => settle(true)}
                                >
                                    {pending.confirmLabel ?? "Confirm"}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                )}
            </Dialog>
        </ConfirmContext.Provider>
    );
};

/**
 * Returns an async `confirm(options)`. Outside a `ConfirmProvider` it falls
 * back to `window.confirm` so the hook is always safe to call.
 */
export function useConfirm(): ConfirmFn {
    const ctx = React.useContext(ConfirmContext);
    return React.useMemo<ConfirmFn>(
        () =>
            ctx ??
            (async (options) => {
                const text = [options.title, options.description]
                    .filter((part) => typeof part === "string")
                    .join("\n\n");
                // eslint-disable-next-line no-alert
                return window.confirm(text || "Are you sure?");
            }),
        [ctx]
    );
}

export default ConfirmProvider;
